import { getT } from '@/i18n';
/**
 * Értesítés-típusok — kliensoldali megjelenítési adatok.
 *
 * A típuskulcsok EGYEZNEK a szerverrel (`server/src/lib/notifications.ts` →
 * `NotificationType`) — ha egy típus itt vagy ott változik, a másikat is
 * frissíteni kell. Nem közös modulban élnek, mert ez a lista tisztán
 * megjelenítési adat (ikon, magyar felirat), a szervernek pedig csak a
 * kulcsra és az alapértelmezett BE/KI állapotra van szüksége — a duplikáció
 * kisebb kockázat, mint egy platform-független modult ráerőltetni egy
 * tisztán UI-célú listára.
 */

export type NotificationType =
  | 'gp_activity'
  | 'activity_saved'
  | 'gp_daily'
  | 'activity_liked'
  | 'activity_commented'
  | 'comment_replied'
  | 'modifier_started'
  | 'badge_awarded'
  | 'passport_country'
  | 'followed_activity'
  | 'new_follower'
  | 'follow_request'
  | 'territory_stolen'
  | 'territory_defended'
  | 'banda_invited'
  | 'banda_membership'
  | 'banda_post'
  | 'banda_wall_reaction'
  | 'banda_chat_message'
  | 'banda_chat_mention'
  | 'banda_daily'
  | 'report_resolved'
  | 'activity_reviewed'
  | 'message'
  | 'message_request'
  | 'streak_reminder'
  | 'mentioned'
  | 'personal_record';

export interface NotificationTypeInfo {
  label: string;
  /** Melyik képernyőre navigáljon a lista-elem koppintásra, ha a szerver nem ad konkrét célt. */
  fallbackScreen: string;
  /** Hiányzó beállításnál ez érvényes; alapból BE. A szerver `NOTIFICATION_TYPES`-ával egyezzen. */
  defaultOn?: boolean;
}

export const NOTIFICATION_TYPES: Record<NotificationType, NotificationTypeInfo> = {
  gp_activity: { get label() { return getT().notificationLabels.gpActivity; }, fallbackScreen: '/profil' },
  activity_saved: { get label() { return getT().notificationLabels.saved; }, fallbackScreen: '/profil' },
  gp_daily: { get label() { return getT().notificationLabels.gpDaily; }, fallbackScreen: '/profil' },
  activity_liked: { get label() { return getT().notificationLabels.liked; }, fallbackScreen: '/profil' },
  activity_commented: { get label() { return getT().notificationLabels.commented; }, fallbackScreen: '/profil' },
  comment_replied: { get label() { return getT().notificationLabels.replied; }, fallbackScreen: '/profil' },
  modifier_started: { get label() { return getT().notificationLabels.boost; }, fallbackScreen: '/beallitasok/szabalyok' },
  badge_awarded: { get label() { return getT().notificationLabels.badge; }, fallbackScreen: '/profil/badgek' },
  passport_country: { get label() { return getT().notificationLabels.passport; }, fallbackScreen: '/profil/utlevel' },
  followed_activity: { get label() { return getT().notificationLabels.followedActivity; }, fallbackScreen: '/kozosseg' },
  new_follower: { get label() { return getT().notificationLabels.follower; }, fallbackScreen: '/profil' },
  follow_request: { get label() { return getT().notificationLabels.followRequest; }, fallbackScreen: '/beallitasok/privat-fiok' },
  territory_stolen: { get label() { return getT().notificationLabels.lost; }, fallbackScreen: '/grund' },
  territory_defended: { get label() { return getT().notificationLabels.defended; }, fallbackScreen: '/grund' },
  banda_invited: { get label() { return getT().notificationLabels.crewInvite; }, fallbackScreen: '/kozosseg/bandak' },
  banda_membership: { get label() { return getT().notificationLabels.crewMembership; }, fallbackScreen: '/kozosseg/bandak' },
  banda_post: { get label() { return getT().notificationLabels.crewPost; }, fallbackScreen: '/kozosseg/bandak' },
  /** KIVEZETETT (#71, F) — csak a régi értesítések megjelenítéséhez; lásd `LEGACY_NOTIFICATION_TYPES`. */
  banda_wall_reaction: { get label() { return getT().notificationLabels.wallReaction; }, fallbackScreen: '/kozosseg/bandak' },
  /** #71: csak push, alapból KI (Geri) — a chatnek saját olvasatlan-jelzése van. */
  banda_chat_message: { get label() { return getT().notificationLabels.crewMessage; }, fallbackScreen: '/kozosseg/bandak', defaultOn: false },
  banda_chat_mention: { get label() { return getT().notificationLabels.crewMention; }, fallbackScreen: '/kozosseg/bandak' },
  banda_daily: { get label() { return getT().notificationLabels.crewDaily; }, fallbackScreen: '/kozosseg/bandak' },
  report_resolved: { get label() { return getT().notificationLabels.report; }, fallbackScreen: '/' },
  activity_reviewed: { get label() { return getT().notificationLabels.review; }, fallbackScreen: '/profil' },
  /** #68: csak push — az értesítéslistába nem kerül, a postafiók maga a lista. */
  message: { get label() { return getT().notificationLabels.message; }, fallbackScreen: '/uzenetek' },
  message_request: { get label() { return getT().notificationLabels.request; }, fallbackScreen: '/uzenetek' },
  /** #69: este 18:00 helyi idő, ha él a sorozat és aznap nincs aktivitás. */
  streak_reminder: { get label() { return getT().notificationLabels.streak; }, fallbackScreen: '/rogzites' },
  /** #69: @felhasználónévvel megemlítettek egy hozzászólásban. */
  mentioned: { get label() { return getT().notificationLabels.mention; }, fallbackScreen: '/profil' },
  personal_record: { get label() { return getT().notificationLabels.record; }, fallbackScreen: '/profil/statisztikak' },
} as const;

/**
 * A KAPCSOLÓK CSOPORTJAI a Beállítások → Értesítések képernyőn.
 *
 * Tizenhat kapcsoló egyetlen listában olvashatatlan: a felhasználó egy
 * konkrét zavaró értesítést keres, és végig kell pásztáznia mindet. A
 * csoportok azt követik, MIRŐL szól az értesítés — a grundról, a saját
 * haladásról, a közösségről vagy a bandáról.
 *
 * ⚠️ MINDEN TÍPUS PONTOSAN EGY CSOPORTBAN van, és ez a lista a sorrend
 * egyetlen forrása is (`NOTIFICATION_TYPE_ORDER` innen származik). Új típus
 * felvételekor ide is be kell tenni, különben nem jelenik meg a képernyőn —
 * a `notificationTypes.test.ts` ezt ellenőrzi.
 */
export interface NotificationGroup {
  title: string;
  types: readonly NotificationType[];
}

export const NOTIFICATION_GROUPS: readonly NotificationGroup[] = [
  {
    /* Az „Aktív akció indul" azért ide tartozik, és nem a pontokhoz: az akció
       a JÁTÉK szabályát változtatja meg egy időre, nem a te teljesítményedről
       szól. A csoport neve ezért nem csak a grundot nevezi meg. */
    get title() { return getT().notificationLabels.gameGroup; },
    types: ['territory_stolen', 'territory_defended', 'modifier_started'],
  },
  {
    get title() { return getT().notificationLabels.pointsGroup; },
    types: ['gp_activity', 'activity_saved', 'gp_daily', 'badge_awarded', 'passport_country', 'streak_reminder', 'personal_record'],
  },
  {
    get title() { return getT().notificationLabels.communityGroup; },
    types: [
      'activity_liked',
      'activity_commented',
      'comment_replied',
      'mentioned',
      'new_follower',
      'follow_request',
      'followed_activity',
    ],
  },
  {
    get title() { return getT().notificationLabels.messageGroup; },
    types: ['message', 'message_request'],
  },
  {
    get title() { return getT().notificationLabels.crewGroup; },
    // A `banda_wall_reaction` kivezetve (#71, F): nincs kapcsolója, de a típus
    // megmarad, mert a régi értesítések listája még hivatkozik rá.
    types: ['banda_invited', 'banda_membership', 'banda_post', 'banda_chat_message', 'banda_chat_mention', 'banda_daily'],
  },
  {
    get title() { return getT().notificationLabels.reviewGroup; },
    types: ['report_resolved', 'activity_reviewed'],
  },
];

/**
 * KIVEZETETT típusok: a szerver már nem állítja elő őket, és kapcsolójuk sincs
 * — a kliens csak azért ismeri őket, mert a régi értesítések listája még
 * hivatkozik rájuk (egy ismeretlen típus a panelt törné). Új típus SOHA ne
 * kerüljön ide; egy típus kivezetésekor ide kerül, nem törlődik.
 */
export const LEGACY_NOTIFICATION_TYPES: readonly NotificationType[] = ['banda_wall_reaction'];

/** Be van-e kapcsolva a típus — a hiányzó beállítás a típus alapértéke (#71 óta nem mindig BE). */
export function isNotificationTypeOn(
  settings: Partial<Record<NotificationType, boolean>>,
  type: NotificationType,
): boolean {
  return settings[type] ?? NOTIFICATION_TYPES[type].defaultOn ?? true;
}

/** A sorrend, ahogy a Beállítások képernyőn a kapcsolók megjelennek. */
export const NOTIFICATION_TYPE_ORDER: readonly NotificationType[] =
  NOTIFICATION_GROUPS.flatMap((group) => group.types);

export interface StoredNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data?: Record<string, string>;
  read: boolean;
  /** Unix ms. */
  createdAt: number;
}

/**
 * Hova visz az értesítésre koppintás.
 *
 * A KOMMENT-értesítések nem csak az aktivitást nyitják meg, hanem a
 * hozzászólás-lapot is (`?komment=1`), és megjelölik a kiemelendő sort
 * (`?kiemelt=<id>`). A `komment` paramétert az `ActivityScreen` már régóta
 * érti (a feed-kártya hozzászólás-gombja is ezt használja) — a `kiemelt`
 * ehhez jött hozzá.
 */
export function screenFor(notification: StoredNotification): string {
  const info = NOTIFICATION_TYPES[notification.type];
  const activityId = notification.data?.activityId;
  const commentId = notification.data?.commentId;
  const username = notification.data?.username;
  const bandaId = notification.data?.bandaId;
  const badgeId = notification.data?.badgeId;

  /* Az új jelvény a Jelvények fülre visz, a jelvényhez (GRUNDO #61). */
  if (notification.type === 'badge_awarded') {
    return badgeId ? `/profil/badgek?jelveny=${encodeURIComponent(badgeId)}` : '/profil/badgek';
  }
  if (activityId) {
    if (commentId) return `/aktivitas/${activityId}?komment=1&kiemelt=${commentId}`;
    if (notification.type === 'activity_commented') return `/aktivitas/${activityId}?komment=1`;
    return `/aktivitas/${activityId}`;
  }
  if (username) return `/felhasznalo/${encodeURIComponent(username)}`;
  /**
   * A MEGHÍVÓ a Bandák oldalra visz, nem a banda oldalára: elfogadni és
   * elutasítani csak ott lehet (`CommunityBandasScreen` → „Meghívóim"). A
   * banda oldala privát bandánál ráadásul még tiltott is a meghívottnak.
   */
  if (notification.type === 'banda_invited') return '/kozosseg/bandak';
  /* Banda chat (#71): az említés és a válasz a chatbe visz, nem a banda oldalára. */
  if (bandaId && (notification.type === 'banda_chat_mention' || notification.type === 'banda_chat_message')) {
    return `/bandak/${encodeURIComponent(bandaId)}/chat`;
  }
  if (bandaId) return `/bandak/${encodeURIComponent(bandaId)}`;
  return info.fallbackScreen;
}
